import { useState, FormEvent } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Avatar,
  Alert,
  Link,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import { HealthAndSafety, Badge, Email, ArrowBack, Send } from '@mui/icons-material';
import { requestPasswordRecovery } from '../../services/auth';
import { cuitDigits } from '../../utils/format';
import { validateCuit } from '../../utils/validation';

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [cuit, setCuit] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    const digits = cuitDigits(cuit);
    if (digits.length !== 11 || !validateCuit(digits)) {
      setError('Ingresa un CUIT valido (11 digitos).');
      return;
    }

    const cleanEmail = email.trim().toLowerCase();
    setSubmitting(true);

    try {
      await requestPasswordRecovery(digits, cleanEmail);
    } catch (_err) {
      setSubmitting(false);
      setError(
        'No pudimos enviar el codigo de recuperacion. Verifica los datos e intenta nuevamente.'
      );
      return;
    }

    setSubmitting(false);

    // Mostramos el mensaje exitoso y damos 1.5 segundos para redirigir al
    // formulario de verificacion con el CUIT + correo pre-cargados.
    setSent(true);
    setTimeout(() => {
      navigate(
        `/auth/reset-password?cuit=${encodeURIComponent(digits)}&email=${encodeURIComponent(cleanEmail)}`
      );
    }, 1500);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background:
          'linear-gradient(135deg, #0d47a1 0%, #1565C0 50%, #42a5f5 100%)',
        p: 2,
      }}
    >
      <Card sx={{ width: 440, maxWidth: '100%', boxShadow: 8 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Avatar
              sx={{
                bgcolor: 'primary.main',
                width: 64,
                height: 64,
                mx: 'auto',
                mb: 2,
              }}
            >
              <HealthAndSafety sx={{ fontSize: 36 }} />
            </Avatar>
            <Typography variant="h5" fontWeight={700} color="primary">
              Recuperar contrasena
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Ingresa tu CUIT y tu correo y te enviaremos un codigo de 6 digitos
              para restablecer tu contrasena.
            </Typography>
          </Box>

          {sent ? (
            <>
              <Alert severity="success" sx={{ mb: 3 }}>
                Si el correo esta registrado, recibiras un codigo de 6 digitos
                en los proximos minutos. Te llevamos al formulario para que lo
                ingreses...
              </Alert>
              <Button
                fullWidth
                component={RouterLink}
                to="/auth/login"
                variant="text"
                startIcon={<ArrowBack />}
              >
                Volver al login
              </Button>
            </>
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              {error && (
                <Alert
                  severity="error"
                  sx={{ mb: 2 }}
                  onClose={() => setError(null)}
                >
                  {error}
                </Alert>
              )}

              <TextField
                fullWidth
                required
                autoFocus
                autoComplete="username"
                label="CUIT"
                type="text"
                inputMode="numeric"
                placeholder="20-12345678-3"
                value={cuit}
                onChange={(e) => setCuit(e.target.value)}
                disabled={submitting}
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Badge color="action" />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                required
                autoComplete="email"
                label="Correo electronico"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={submitting}
                sx={{ mb: 3 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email color="action" />
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                fullWidth
                type="submit"
                variant="contained"
                size="large"
                disabled={submitting || !cuit || !email}
                startIcon={
                  submitting ? <CircularProgress size={20} color="inherit" /> : <Send />
                }
                sx={{ py: 1.5, mb: 2 }}
              >
                {submitting ? 'Enviando...' : 'Enviar codigo'}
              </Button>

              <Box sx={{ textAlign: 'center' }}>
                <Link
                  component={RouterLink}
                  to="/auth/login"
                  variant="body2"
                  underline="hover"
                >
                  Volver al login
                </Link>
              </Box>
            </form>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
